export type TClipboardAvailability = 'available' | 'unavailable';

/**
 * Detects whether this browser exposes a usable `navigator.clipboard.writeText`.
 *
 * **This sees exactly one class of failure: the API not being there.** That covers an insecure
 * context, where the browser omits `navigator.clipboard` outright, and a non-browser evaluation
 * where `navigator` itself is absent. Checking up front means a user in that situation is handed
 * the fallback immediately rather than made to watch a copy fail.
 *
 * It cannot see the other class, where the API is present and the write is refused anyway: a denied
 * permission, an unfocused document, or an embedded browser with a restrictive Permissions-Policy.
 * Those report a clipboard and reject at call time, so `RequestLinkAction` catches them from
 * `writeText` instead. Do not read a false positive here as a bug; it is the half this cannot know.
 *
 * Pulled out as a pure function, separate from the component, so it can be exercised directly in
 * Vitest without mounting anything or driving React state (ADR 0104).
 */
export function detectClipboardAvailability(): TClipboardAvailability {
  const hasWritableClipboard =
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard !== 'undefined' &&
    typeof navigator.clipboard.writeText === 'function';

  return hasWritableClipboard ? 'available' : 'unavailable';
}
